import React, { useContext, useEffect, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline, GlobalStyles } from "@mui/material";
import { AuthContext } from "../context/AuthContext";
import { ColorModeContext, THEME_MODES } from "../theme/ThemeProvider";
import Dashboard from "./Dashboard";
import { operationalTheme } from "../theme/operationalTheme";
import { DASHBOARD_STYLE_STORAGE_KEY, DASHBOARD_STYLE_SYNC_EVENT } from "./Settings";

// Color configurations for retro themes
const RETRO_COLORS = {
  green: {
    primary: "#00ff00",
    primaryLight: "#33ff33",
    primaryDark: "#00cc00",
    disabled: "#006600",
    divider: "#003300",
    bgDark: "#002200",
    bgMedium: "#003300",
    bgLight: "#004400",
    rgb: "0, 255, 0",
  },
  red: {
    primary: "#ff3333",
    primaryLight: "#ff6666",
    primaryDark: "#cc0000",
    disabled: "#660000",
    divider: "#330000",
    bgDark: "#220000",
    bgMedium: "#330000",
    bgLight: "#440000",
    rgb: "255, 51, 51",
  },
};

// Retro theme that mimics late 90s/early 2000s police CAD/MDT systems
const createRetroTheme = (color = "green") => {
  const c = RETRO_COLORS[color] || RETRO_COLORS.green;
  
  return operationalTheme(createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: c.primary,
        light: c.primaryLight,
        dark: c.primaryDark,
        contrastText: "#000000",
      },
      secondary: {
        main: "#ffcc00", // Amber/gold for highlights
        light: "#ffdd44",
        dark: "#cc9900",
        contrastText: "#000000",
      },
      background: {
        default: "#0a0a0a", // Near black
        paper: "#111111", // Slightly lighter black
      },
      error: {
        main: "#ff3333",
        light: "#ff6666",
        dark: "#cc0000",
      },
      warning: {
        main: "#ffcc00",
        light: "#ffdd44",
        dark: "#cc9900",
      },
      success: {
        main: "#00ff00",
        light: "#33ff33",
        dark: "#00cc00",
      },
      info: {
        main: "#00ccff",
        light: "#33ddff",
        dark: "#0099cc",
      },
      text: {
        primary: "#e6ece9",
        secondary: "#b7c4bd",
        disabled: c.disabled,
      },
      divider: c.divider,
      action: {
        active: c.primary,
        hover: `rgba(${c.rgb}, 0.1)`,
        selected: `rgba(${c.rgb}, 0.2)`,
        disabled: c.bgLight,
        disabledBackground: c.bgDark,
      },
    },
    typography: {
      fontFamily: '"Courier New", "Lucida Console", Monaco, monospace',
      fontSize: 14,
      h1: { fontFamily: '"Courier New", monospace', fontWeight: 700, letterSpacing: "0.05em" },
      h2: { fontFamily: '"Courier New", monospace', fontWeight: 700, letterSpacing: "0.05em" },
      h3: { fontFamily: '"Courier New", monospace', fontWeight: 700, letterSpacing: "0.04em" },
      h4: { fontFamily: '"Courier New", monospace', fontWeight: 700, letterSpacing: "0.03em" },
      h5: { fontFamily: '"Courier New", monospace', fontWeight: 600, letterSpacing: "0.02em" },
      h6: { fontFamily: '"Courier New", monospace', fontWeight: 600, letterSpacing: "0.01em" },
      subtitle1: { fontFamily: '"Courier New", monospace', letterSpacing: "0.02em" },
      subtitle2: { fontFamily: '"Courier New", monospace', letterSpacing: "0.02em" },
      body1: { fontFamily: '"Courier New", monospace', letterSpacing: "0.01em" },
      body2: { fontFamily: '"Courier New", monospace', letterSpacing: "0.01em" },
      button: { fontFamily: '"Courier New", monospace', fontWeight: 600, letterSpacing: "0.05em" },
      caption: { fontFamily: '"Courier New", monospace', letterSpacing: "0.02em" },
      overline: { fontFamily: '"Courier New", monospace', letterSpacing: "0.1em" },
    },
    shape: {
      borderRadius: 0, // No rounded corners - very blocky
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: "#0a0a0a",
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: "#111111",
            border: `2px solid ${c.primary}`,
            borderRadius: 0,
            boxShadow: `0 0 10px rgba(${c.rgb}, 0.3)`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: "#111111",
            border: `2px solid ${c.primary}`,
            borderRadius: 0,
            boxShadow: `0 0 8px rgba(${c.rgb}, 0.2)`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            border: "2px solid",
            textTransform: "uppercase",
            fontWeight: 700,
            letterSpacing: "0.1em",
            boxShadow: "none",
            "&:hover": {
              boxShadow: `0 0 10px rgba(${c.rgb}, 0.5)`,
            },
          },
          contained: {
            backgroundColor: c.bgMedium,
            color: c.primary,
            borderColor: c.primary,
            "&:hover": {
              backgroundColor: c.bgLight,
            },
          },
          containedPrimary: {
            backgroundColor: c.bgMedium,
            color: c.primary,
            borderColor: c.primary,
            "&:hover": {
              backgroundColor: c.bgLight,
            },
          },
          containedSecondary: {
            backgroundColor: "#332200",
            color: "#ffcc00",
            borderColor: "#ffcc00",
            "&:hover": {
              backgroundColor: "#443300",
            },
          },
          containedError: {
            backgroundColor: "#330000",
            color: "#ff3333",
            borderColor: "#ff3333",
            "&:hover": {
              backgroundColor: "#440000",
            },
          },
          containedSuccess: {
            backgroundColor: "#003300",
            color: "#00ff00",
            borderColor: "#00ff00",
            "&:hover": {
              backgroundColor: "#004400",
            },
          },
          containedWarning: {
            backgroundColor: "#332200",
            color: "#ffcc00",
            borderColor: "#ffcc00",
            "&:hover": {
              backgroundColor: "#443300",
            },
          },
          outlined: {
            borderWidth: "2px",
            "&:hover": {
              borderWidth: "2px",
            },
          },
          outlinedPrimary: {
            borderColor: c.primary,
            color: c.primary,
            "&:hover": {
              backgroundColor: `rgba(${c.rgb}, 0.1)`,
              borderColor: c.primary,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              backgroundColor: "#000000",
              "& fieldset": {
                borderColor: c.primary,
                borderWidth: "2px",
              },
              "&:hover fieldset": {
                borderColor: c.primaryLight,
              },
              "&.Mui-focused fieldset": {
                borderColor: c.primary,
                boxShadow: `0 0 8px rgba(${c.rgb}, 0.5)`,
              },
            },
            "& .MuiInputBase-input": {
              color: c.primary,
              fontFamily: '"Courier New", monospace',
            },
            "& .MuiInputLabel-root": {
              color: c.primaryDark,
              fontFamily: '"Courier New", monospace',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primary,
              borderWidth: "2px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primaryLight,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primary,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            backgroundColor: "#000000",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primary,
              borderWidth: "2px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primaryLight,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primary,
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: "separate",
            borderSpacing: 0,
            border: `2px solid ${c.primary}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${c.divider}`,
            borderRight: `1px solid ${c.divider}`,
            fontFamily: '"Courier New", monospace',
            padding: "8px 12px",
            "&:last-child": {
              borderRight: "none",
            },
          },
          head: {
            backgroundColor: c.bgDark,
            color: c.primary,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            borderBottom: `2px solid ${c.primary}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: `rgba(${c.rgb}, 0.05) !important`,
            },
            "&.Mui-selected": {
              backgroundColor: `rgba(${c.rgb}, 0.15) !important`,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            fontFamily: '"Courier New", monospace',
            fontWeight: 600,
            letterSpacing: "0.05em",
            border: "1px solid",
          },
          colorPrimary: {
            backgroundColor: c.bgDark,
            color: c.primary,
            borderColor: c.primary,
          },
          colorSecondary: {
            backgroundColor: "#221100",
            color: "#ffcc00",
            borderColor: "#ffcc00",
          },
          colorError: {
            backgroundColor: "#220000",
            color: "#ff3333",
            borderColor: "#ff3333",
          },
          colorSuccess: {
            backgroundColor: "#002200",
            color: "#00ff00",
            borderColor: "#00ff00",
          },
          colorWarning: {
            backgroundColor: "#221100",
            color: "#ffcc00",
            borderColor: "#ffcc00",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `3px solid ${c.primary}`,
            borderRadius: 0,
            backgroundColor: "#0a0a0a",
            boxShadow: `0 0 30px rgba(${c.rgb}, 0.4)`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgDark,
            borderBottom: `2px solid ${c.primary}`,
            fontFamily: '"Courier New", monospace',
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: "#0a0a0a",
            borderBottom: `2px solid ${c.primary}`,
            boxShadow: `0 0 15px rgba(${c.rgb}, 0.3)`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: "#0a0a0a",
            borderRight: `2px solid ${c.primary}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            "&:hover": {
              backgroundColor: `rgba(${c.rgb}, 0.1)`,
            },
            "&.Mui-selected": {
              backgroundColor: `rgba(${c.rgb}, 0.2)`,
              borderLeft: `4px solid ${c.primary}`,
              "&:hover": {
                backgroundColor: `rgba(${c.rgb}, 0.25)`,
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: c.primary,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: c.divider,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            border: "2px solid",
            fontFamily: '"Courier New", monospace',
          },
          standardInfo: {
            backgroundColor: "#001122",
            color: "#00ccff",
            borderColor: "#00ccff",
          },
          standardSuccess: {
            backgroundColor: "#002200",
            color: "#00ff00",
            borderColor: "#00ff00",
          },
          standardWarning: {
            backgroundColor: "#221100",
            color: "#ffcc00",
            borderColor: "#ffcc00",
          },
          standardError: {
            backgroundColor: "#220000",
            color: "#ff3333",
            borderColor: "#ff3333",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .MuiSwitch-track": {
              backgroundColor: c.divider,
            },
            "& .Mui-checked + .MuiSwitch-track": {
              backgroundColor: `${c.primary} !important`,
            },
            "& .MuiSwitch-thumb": {
              backgroundColor: c.primary,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: c.primary,
            "&.Mui-checked": {
              color: c.primary,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: c.primary,
            "&.Mui-checked": {
              color: c.primary,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: c.primary,
            "&:hover": {
              backgroundColor: `rgba(${c.rgb}, 0.1)`,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: "#0a0a0a",
            border: `2px solid ${c.primary}`,
            borderRadius: 0,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: '"Courier New", monospace',
            "&:hover": {
              backgroundColor: `rgba(${c.rgb}, 0.1)`,
            },
            "&.Mui-selected": {
              backgroundColor: `rgba(${c.rgb}, 0.2)`,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: c.bgDark,
            color: c.primary,
            border: `1px solid ${c.primary}`,
            borderRadius: 0,
            fontFamily: '"Courier New", monospace',
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            "& .MuiPaper-root": {
              borderRadius: 0,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: c.primary,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgDark,
            borderRadius: 0,
          },
          bar: {
            backgroundColor: c.primary,
            borderRadius: 0,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: `rgba(${color === "red" ? "10, 0, 0" : "0, 10, 0"}, 0.8)`,
          },
        },
      },
    },
  }));
};

// Windows XP Theme - supports light (Luna Blue) and dark variants with color options
const createWindowsXPTheme = (isDark = false, colorVariant = "blue") => {
  // Color schemes for different variants
  const colorSchemes = {
    blue: {
      primary: isDark ? "#4A9EFF" : "#0055E5",
      primaryLight: isDark ? "#7BB8FF" : "#3388FF",
      primaryDark: isDark ? "#2B7FE0" : "#003399",
      secondary: isDark ? "#00CC66" : "#008000",
      titleBar: isDark 
        ? "linear-gradient(180deg, #2A2A4A 0%, #1A1A2E 100%)"
        : "linear-gradient(180deg, #0A246A 0%, #0055E5 8%, #0055E5 92%, #0A246A 100%)",
      accent: isDark ? "#4A9EFF" : "#0055E5",
      accentRgb: isDark ? "74, 158, 255" : "0, 85, 229",
    },
    green: {
      primary: "#00FF00",
      primaryLight: "#33FF33",
      primaryDark: "#00CC00",
      secondary: "#00CC00",
      titleBar: isDark 
        ? "linear-gradient(180deg, #002200 0%, #003300 50%, #001100 100%)"
        : "linear-gradient(180deg, #004400 0%, #006600 8%, #006600 92%, #004400 100%)",
      accent: "#00FF00",
      accentRgb: "0, 255, 0",
    },
    red: {
      primary: "#FF3333",
      primaryLight: "#FF6666",
      primaryDark: "#CC0000",
      secondary: "#FF6600",
      titleBar: isDark 
        ? "linear-gradient(180deg, #220000 0%, #330000 50%, #110000 100%)"
        : "linear-gradient(180deg, #440000 0%, #660000 8%, #660000 92%, #440000 100%)",
      accent: "#FF3333",
      accentRgb: "255, 51, 51",
    },
  };

  const scheme = colorSchemes[colorVariant] || colorSchemes.blue;

  // XP Royale Noir (dark) vs Luna Blue (light)
  const colors = isDark ? {
    bg: colorVariant === "green" ? "#001100" : colorVariant === "red" ? "#110000" : "#1A1A2E",
    paper: colorVariant === "green" ? "#002200" : colorVariant === "red" ? "#220000" : "#252542",
    primary: scheme.primary,
    primaryLight: scheme.primaryLight,
    primaryDark: scheme.primaryDark,
    secondary: scheme.secondary,
    text: colorVariant === "green" ? "#00FF00" : colorVariant === "red" ? "#FF6666" : "#E0E0E0",
    textSecondary: colorVariant === "green" ? "#00CC00" : colorVariant === "red" ? "#CC4444" : "#A0A0A0",
    border: colorVariant === "green" ? "#003300" : colorVariant === "red" ? "#330000" : "#3A3A5A",
    divider: colorVariant === "green" ? "#003300" : colorVariant === "red" ? "#330000" : "#3A3A5A",
    titleBar: scheme.titleBar,
    buttonBg: colorVariant === "green" 
      ? "linear-gradient(180deg, #003300 0%, #002200 50%, #001100 100%)"
      : colorVariant === "red"
      ? "linear-gradient(180deg, #330000 0%, #220000 50%, #110000 100%)"
      : "linear-gradient(180deg, #3A3A5A 0%, #2A2A4A 50%, #1A1A2E 100%)",
    buttonHover: colorVariant === "green"
      ? "linear-gradient(180deg, #004400 0%, #003300 50%, #002200 100%)"
      : colorVariant === "red"
      ? "linear-gradient(180deg, #440000 0%, #330000 50%, #220000 100%)"
      : "linear-gradient(180deg, #4A4A6A 0%, #3A3A5A 50%, #2A2A4A 100%)",
    inputBg: colorVariant === "green" ? "#001100" : colorVariant === "red" ? "#110000" : "#1A1A2E",
    tableBg: colorVariant === "green" ? "#002200" : colorVariant === "red" ? "#220000" : "#252542",
    tableRowAlt: colorVariant === "green" ? "#001800" : colorVariant === "red" ? "#180000" : "#2A2A4A",
    tableRowHover: colorVariant === "green" ? "#003300" : colorVariant === "red" ? "#330000" : "#3A3A5A",
    scrollBg: colorVariant === "green" ? "#002200" : colorVariant === "red" ? "#220000" : "#2A2A4A",
    scrollThumb: colorVariant === "green"
      ? "linear-gradient(90deg, #004400 0%, #003300 50%, #002200 100%)"
      : colorVariant === "red"
      ? "linear-gradient(90deg, #440000 0%, #330000 50%, #220000 100%)"
      : "linear-gradient(90deg, #4A4A6A 0%, #3A3A5A 50%, #2A2A4A 100%)",
    accentRgb: scheme.accentRgb,
  } : {
    bg: "#ECE9D8",
    paper: "#FFFFFF",
    primary: scheme.primary,
    primaryLight: scheme.primaryLight,
    primaryDark: scheme.primaryDark,
    secondary: scheme.secondary,
    text: "#000000",
    textSecondary: "#333333",
    border: "#ACA899",
    divider: "#ACA899",
    titleBar: scheme.titleBar,
    buttonBg: "linear-gradient(180deg, #FFFFFF 0%, #ECE9D8 50%, #D4D0C8 100%)",
    buttonHover: "linear-gradient(180deg, #FFFFFF 0%, #F0EDE4 50%, #E4E0D8 100%)",
    inputBg: "#FFFFFF",
    tableBg: "#FFFFFF",
    tableRowAlt: "#F5F5F0",
    tableRowHover: "#E8E4D8",
    scrollBg: "#F0F0F0",
    scrollThumb: "linear-gradient(90deg, #F0F0F0 0%, #D4D4D4 50%, #C0C0C0 100%)",
    accentRgb: scheme.accentRgb,
  };

  return createTheme({
    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryDark,
        contrastText: colorVariant === "green" || colorVariant === "red" ? "#000000" : "#FFFFFF",
      },
      secondary: {
        main: colors.secondary,
        light: isDark ? "#33DD88" : "#22AA22",
        dark: isDark ? "#009944" : "#006600",
        contrastText: "#FFFFFF",
      },
      background: {
        default: colors.bg,
        paper: colors.paper,
      },
      error: {
        main: "#CC0000",
        light: "#FF3333",
        dark: "#990000",
      },
      warning: {
        main: "#FF9900",
        light: "#FFBB33",
        dark: "#CC7700",
      },
      success: {
        main: isDark ? "#00CC66" : "#008000",
        light: isDark ? "#33DD88" : "#22AA22",
        dark: isDark ? "#009944" : "#006600",
      },
      info: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryDark,
      },
      text: {
        primary: colors.text,
        secondary: colors.textSecondary,
        disabled: isDark ? "#606060" : "#808080",
      },
      divider: colors.divider,
      action: {
        active: colors.primary,
        hover: `rgba(${colors.accentRgb}, 0.15)`,
        selected: `rgba(${colors.accentRgb}, 0.25)`,
        disabled: isDark ? "#505050" : "#C0C0C0",
        disabledBackground: isDark ? "#303030" : "#E0E0E0",
      },
    },
    typography: {
      fontFamily: '"Tahoma", "Segoe UI", "Arial", sans-serif',
      fontSize: 13,
      h1: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 700 },
      h2: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 700 },
      h3: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 700 },
      h4: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 700 },
      h5: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 600 },
      h6: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 600 },
      subtitle1: { fontFamily: '"Tahoma", "Arial", sans-serif' },
      subtitle2: { fontFamily: '"Tahoma", "Arial", sans-serif' },
      body1: { fontFamily: '"Tahoma", "Arial", sans-serif' },
      body2: { fontFamily: '"Tahoma", "Arial", sans-serif' },
      button: { fontFamily: '"Tahoma", "Arial", sans-serif', fontWeight: 600, textTransform: "none" },
      caption: { fontFamily: '"Tahoma", "Arial", sans-serif' },
      overline: { fontFamily: '"Tahoma", "Arial", sans-serif' },
    },
    shape: {
      borderRadius: 3,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.bg,
            backgroundImage: "none",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: colors.titleBar,
            boxShadow: isDark 
              ? "0 2px 4px rgba(0, 0, 0, 0.5)" 
              : "0 2px 4px rgba(0, 0, 0, 0.3)",
            borderBottom: `1px solid ${isDark ? "#4A4A6A" : "#003399"}`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            "& .MuiIconButton-root": {
              color: "#FFFFFF",
            },
            "& .MuiButton-root": {
              color: "#FFFFFF",
              borderColor: "rgba(255, 255, 255, 0.5)",
              "&:hover": {
                borderColor: "#FFFFFF",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            },
            "& .MuiTypography-root": {
              color: "#FFFFFF",
            },
            "& .MuiBadge-badge": {
              backgroundColor: "#FF3333",
              color: "#FFFFFF",
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.paper,
            borderRight: `1px solid ${colors.border}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: colors.paper,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: isDark 
              ? "2px 2px 4px rgba(0, 0, 0, 0.4)"
              : "2px 2px 3px rgba(0, 0, 0, 0.2)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: colors.paper,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: isDark 
              ? "2px 2px 4px rgba(0, 0, 0, 0.4)"
              : "2px 2px 3px rgba(0, 0, 0, 0.2)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            minHeight: 23,
            padding: "2px 12px",
            border: "1px solid",
            borderTopColor: isDark ? "#5A5A7A" : "#FFFFFF",
            borderLeftColor: isDark ? "#5A5A7A" : "#FFFFFF",
            borderRightColor: isDark ? "#1A1A2E" : "#404040",
            borderBottomColor: isDark ? "#1A1A2E" : "#404040",
            "&:hover": {
              boxShadow: "none",
            },
            "&:active": {
              borderTopColor: isDark ? "#1A1A2E" : "#404040",
              borderLeftColor: isDark ? "#1A1A2E" : "#404040",
              borderRightColor: isDark ? "#5A5A7A" : "#FFFFFF",
              borderBottomColor: isDark ? "#5A5A7A" : "#FFFFFF",
            },
          },
          contained: {
            background: colors.buttonBg,
            color: colors.text,
            "&:hover": {
              background: colors.buttonHover,
            },
          },
          containedPrimary: {
            background: isDark
              ? "linear-gradient(180deg, #5AAEFF 0%, #4A9EFF 50%, #3A8EEF 100%)"
              : "linear-gradient(180deg, #4A9EFF 0%, #0055E5 50%, #0044BB 100%)",
            color: "#FFFFFF",
            borderTopColor: isDark ? "#7BBEFF" : "#6AB4FF",
            borderLeftColor: isDark ? "#7BBEFF" : "#6AB4FF",
            borderRightColor: isDark ? "#2A6ECF" : "#003399",
            borderBottomColor: isDark ? "#2A6ECF" : "#003399",
            "&:hover": {
              background: isDark
                ? "linear-gradient(180deg, #6BBEFF 0%, #5AAEFF 50%, #4A9EFF 100%)"
                : "linear-gradient(180deg, #6AB4FF 0%, #1166F5 50%, #0055CC 100%)",
            },
          },
          containedSecondary: {
            background: isDark
              ? "linear-gradient(180deg, #33DD88 0%, #00CC66 50%, #00AA55 100%)"
              : "linear-gradient(180deg, #33CC33 0%, #008000 50%, #006600 100%)",
            color: "#FFFFFF",
            borderTopColor: isDark ? "#55EE99" : "#55DD55",
            borderLeftColor: isDark ? "#55EE99" : "#55DD55",
            borderRightColor: isDark ? "#008844" : "#004400",
            borderBottomColor: isDark ? "#008844" : "#004400",
            "&:hover": {
              background: isDark
                ? "linear-gradient(180deg, #44EE99 0%, #11DD77 50%, #00BB66 100%)"
                : "linear-gradient(180deg, #44DD44 0%, #119911 50%, #007700 100%)",
            },
          },
          containedError: {
            background: "linear-gradient(180deg, #FF6666 0%, #CC0000 50%, #AA0000 100%)",
            color: "#FFFFFF",
            borderTopColor: "#FF8888",
            borderLeftColor: "#FF8888",
            borderRightColor: "#880000",
            borderBottomColor: "#880000",
            "&:hover": {
              background: "linear-gradient(180deg, #FF7777 0%, #DD1111 50%, #BB0000 100%)",
            },
          },
          containedSuccess: {
            background: isDark
              ? "linear-gradient(180deg, #33DD88 0%, #00CC66 50%, #00AA55 100%)"
              : "linear-gradient(180deg, #33CC33 0%, #008000 50%, #006600 100%)",
            color: "#FFFFFF",
            borderTopColor: isDark ? "#55EE99" : "#55DD55",
            borderLeftColor: isDark ? "#55EE99" : "#55DD55",
            borderRightColor: isDark ? "#008844" : "#004400",
            borderBottomColor: isDark ? "#008844" : "#004400",
            "&:hover": {
              background: isDark
                ? "linear-gradient(180deg, #44EE99 0%, #11DD77 50%, #00BB66 100%)"
                : "linear-gradient(180deg, #44DD44 0%, #119911 50%, #007700 100%)",
            },
          },
          containedWarning: {
            background: "linear-gradient(180deg, #FFCC44 0%, #FF9900 50%, #DD8800 100%)",
            color: "#000000",
            borderTopColor: "#FFDD66",
            borderLeftColor: "#FFDD66",
            borderRightColor: "#BB6600",
            borderBottomColor: "#BB6600",
            "&:hover": {
              background: "linear-gradient(180deg, #FFDD55 0%, #FFAA11 50%, #EE9900 100%)",
            },
          },
          containedInherit: {
            background: colors.buttonBg,
            color: colors.text,
            "&:hover": {
              background: colors.buttonHover,
            },
          },
          outlined: {
            borderWidth: "1px",
            background: isDark 
              ? "linear-gradient(180deg, #3A3A5A 0%, #2A2A4A 100%)"
              : "linear-gradient(180deg, #FFFFFF 0%, #F5F5F0 100%)",
            color: colors.text,
            "&:hover": {
              borderWidth: "1px",
              background: isDark
                ? "linear-gradient(180deg, #4A4A6A 0%, #3A3A5A 100%)"
                : "linear-gradient(180deg, #FFFFFF 0%, #FAFAF5 100%)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              backgroundColor: colors.inputBg,
              "& fieldset": {
                borderColor: isDark ? "#4A4A6A" : "#7F9DB9",
                borderWidth: "1px",
                boxShadow: isDark 
                  ? "inset 1px 1px 3px rgba(0, 0, 0, 0.3)"
                  : "inset 1px 1px 2px rgba(0, 0, 0, 0.15)",
              },
              "&:hover fieldset": {
                borderColor: colors.primary,
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.primary,
                borderWidth: "1px",
              },
            },
            "& .MuiInputBase-input": {
              color: colors.text,
              fontFamily: '"Tahoma", "Arial", sans-serif',
            },
            "& .MuiInputLabel-root": {
              color: colors.textSecondary,
              fontFamily: '"Tahoma", "Arial", sans-serif',
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: colors.primary,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.textSecondary,
            fontFamily: '"Tahoma", "Arial", sans-serif',
            "&.Mui-focused": {
              color: colors.primary,
            },
          },
          outlined: {
            // Fix label positioning for outlined inputs
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, -9px) scale(0.75)",
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#4A4A6A" : "#7F9DB9",
              borderWidth: "1px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
            },
          },
          select: {
            color: colors.text,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            backgroundColor: colors.inputBg,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#4A4A6A" : "#7F9DB9",
              borderWidth: "1px",
              boxShadow: isDark
                ? "inset 1px 1px 3px rgba(0, 0, 0, 0.3)"
                : "inset 1px 1px 2px rgba(0, 0, 0, 0.15)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: "1px",
            },
          },
          input: {
            color: colors.text,
            fontFamily: '"Tahoma", "Arial", sans-serif',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: "separate",
            borderSpacing: 0,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.tableBg,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${isDark ? "#3A3A5A" : "#D4D0C8"}`,
            borderRight: `1px solid ${isDark ? "#3A3A5A" : "#D4D0C8"}`,
            fontFamily: '"Tahoma", "Arial", sans-serif',
            padding: "4px 8px",
            fontSize: "12px",
            color: colors.text,
            "&:last-child": {
              borderRight: "none",
            },
          },
          head: {
            backgroundColor: isDark ? "#2A2A4A" : "#ECE9D8",
            fontWeight: 700,
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-root": {
              background: isDark
                ? "linear-gradient(180deg, #3A3A5A 0%, #2A2A4A 100%)"
                : "linear-gradient(180deg, #FFFFFF 0%, #ECE9D8 100%)",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: colors.tableRowAlt,
            },
            "&:hover": {
              backgroundColor: `${colors.tableRowHover} !important`,
            },
            "&.Mui-selected": {
              backgroundColor: "#316AC5 !important",
              "& .MuiTableCell-root": {
                color: "#FFFFFF",
              },
              "&:hover": {
                backgroundColor: "#316AC5 !important",
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 3,
            fontFamily: '"Tahoma", "Arial", sans-serif',
            border: "1px solid",
            borderTopColor: isDark ? "#5A5A7A" : "#FFFFFF",
            borderLeftColor: isDark ? "#5A5A7A" : "#FFFFFF",
            borderRightColor: isDark ? "#2A2A4A" : "#808080",
            borderBottomColor: isDark ? "#2A2A4A" : "#808080",
          },
          colorPrimary: {
            background: isDark
              ? "linear-gradient(180deg, #5AAEFF 0%, #4A9EFF 100%)"
              : "linear-gradient(180deg, #6AB4FF 0%, #0055E5 100%)",
            color: "#FFFFFF",
          },
          colorSecondary: {
            background: isDark
              ? "linear-gradient(180deg, #33DD88 0%, #00CC66 100%)"
              : "linear-gradient(180deg, #44DD44 0%, #008000 100%)",
            color: "#FFFFFF",
          },
          colorError: {
            background: "linear-gradient(180deg, #FF7777 0%, #CC0000 100%)",
            color: "#FFFFFF",
          },
          colorWarning: {
            background: "linear-gradient(180deg, #FFCC44 0%, #FF9900 100%)",
            color: "#000000",
          },
          colorSuccess: {
            background: isDark
              ? "linear-gradient(180deg, #33DD88 0%, #00CC66 100%)"
              : "linear-gradient(180deg, #44DD44 0%, #008000 100%)",
            color: "#FFFFFF",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 3,
            border: `1px solid ${colors.primary}`,
            boxShadow: isDark 
              ? "4px 4px 12px rgba(0, 0, 0, 0.5)"
              : "4px 4px 8px rgba(0, 0, 0, 0.3)",
            backgroundColor: colors.paper,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            background: colors.titleBar,
            color: "#FFFFFF",
            fontWeight: 700,
            fontFamily: '"Tahoma", "Arial", sans-serif',
            padding: "8px 16px",
            fontSize: "13px",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: colors.bg,
            padding: "16px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: colors.bg,
            borderTop: `1px solid ${colors.border}`,
            padding: "8px 16px",
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 3,
            border: "1px solid",
          },
          standardInfo: {
            backgroundColor: isDark ? "#1A2A4A" : "#E5F3FF",
            color: isDark ? "#7BB8FF" : "#0055E5",
            borderColor: colors.primary,
          },
          standardSuccess: {
            backgroundColor: isDark ? "#1A3A2A" : "#E5FFE5",
            color: isDark ? "#33DD88" : "#008000",
            borderColor: isDark ? "#00CC66" : "#008000",
          },
          standardWarning: {
            backgroundColor: isDark ? "#3A2A1A" : "#FFF5E5",
            color: isDark ? "#FFBB33" : "#996600",
            borderColor: "#FF9900",
          },
          standardError: {
            backgroundColor: isDark ? "#3A1A1A" : "#FFE5E5",
            color: isDark ? "#FF6666" : "#CC0000",
            borderColor: "#CC0000",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .MuiSwitch-track": {
              backgroundColor: isDark ? "#3A3A5A" : "#ACA899",
              borderRadius: 3,
            },
            "& .Mui-checked + .MuiSwitch-track": {
              backgroundColor: `${colors.primary} !important`,
            },
            "& .MuiSwitch-thumb": {
              background: isDark
                ? "linear-gradient(180deg, #5A5A7A 0%, #3A3A5A 100%)"
                : "linear-gradient(180deg, #FFFFFF 0%, #D4D0C8 100%)",
              borderRadius: 2,
              boxShadow: "1px 1px 2px rgba(0, 0, 0, 0.2)",
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: colors.primary,
            "&.Mui-checked": {
              color: colors.primary,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colors.primary,
            "&.Mui-checked": {
              color: colors.primary,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: colors.primary,
            "&:hover": {
              backgroundColor: isDark ? "rgba(74, 158, 255, 0.15)" : "rgba(0, 85, 229, 0.1)",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.paper,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
            boxShadow: isDark 
              ? "2px 2px 6px rgba(0, 0, 0, 0.4)"
              : "2px 2px 4px rgba(0, 0, 0, 0.25)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: '"Tahoma", "Arial", sans-serif',
            fontSize: "12px",
            padding: "4px 16px",
            color: colors.text,
            "&:hover": {
              backgroundColor: "#316AC5",
              color: "#FFFFFF",
            },
            "&.Mui-selected": {
              backgroundColor: "#316AC5",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#316AC5",
              },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? "#3A3A5A" : "#FFFFE1",
            color: isDark ? "#E0E0E0" : "#000000",
            border: `1px solid ${isDark ? "#5A5A7A" : "#000000"}`,
            borderRadius: 0,
            fontFamily: '"Tahoma", "Arial", sans-serif',
            fontSize: "11px",
            boxShadow: "2px 2px 3px rgba(0, 0, 0, 0.2)",
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            "& .MuiPaper-root": {
              borderRadius: 3,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: colors.primary,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#2A2A4A" : "#D4D0C8",
            borderRadius: 0,
            height: 16,
            border: `1px solid ${isDark ? "#4A4A6A" : "#808080"}`,
            boxShadow: isDark
              ? "inset 1px 1px 3px rgba(0, 0, 0, 0.3)"
              : "inset 1px 1px 2px rgba(0, 0, 0, 0.15)",
          },
          bar: {
            background: "repeating-linear-gradient(90deg, #00CC00 0px, #00CC00 8px, #00AA00 8px, #00AA00 10px)",
            borderRadius: 0,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.4)",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.divider,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 28,
          },
          indicator: {
            display: "none",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontFamily: '"Tahoma", "Arial", sans-serif',
            fontSize: "12px",
            minHeight: 28,
            padding: "4px 12px",
            borderRadius: "3px 3px 0 0",
            border: `1px solid ${colors.border}`,
            borderBottom: "none",
            marginRight: "2px",
            color: colors.text,
            background: isDark
              ? "linear-gradient(180deg, #3A3A5A 0%, #2A2A4A 100%)"
              : "linear-gradient(180deg, #FFFFFF 0%, #ECE9D8 100%)",
            "&.Mui-selected": {
              background: colors.paper,
              borderBottomColor: colors.paper,
              zIndex: 1,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: colors.text,
            "&:hover": {
              backgroundColor: isDark ? "rgba(74, 158, 255, 0.1)" : "rgba(0, 85, 229, 0.08)",
            },
            "&.active": {
              backgroundColor: colors.primary,
              color: "#FFFFFF",
              "& .MuiListItemText-primary": {
                color: "#FFFFFF",
              },
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: colors.text,
          },
        },
      },
    },
  });
};

// Global styles for Windows XP look
const createWindowsXPGlobalStyles = (isDark = false, colorVariant = "blue") => {
  const colorSchemes = {
    blue: {
      bg: isDark ? "#1A1A2E" : "#ECE9D8",
      text: isDark ? "#E0E0E0" : "#000000",
      link: isDark ? "#7BB8FF" : "#0055E5",
      linkHover: isDark ? "#9CCFFF" : "#0066FF",
      selection: isDark ? "#4A6A95" : "#316AC5",
      scrollBg: isDark ? "#2A2A4A" : "#F0F0F0",
      scrollThumb: isDark 
        ? "linear-gradient(90deg, #4A4A6A 0%, #3A3A5A 50%, #2A2A4A 100%)"
        : "linear-gradient(90deg, #F0F0F0 0%, #D4D4D4 50%, #C0C0C0 100%)",
      scrollThumbHover: isDark
        ? "linear-gradient(90deg, #5A5A7A 0%, #4A4A6A 50%, #3A3A5A 100%)"
        : "linear-gradient(90deg, #FFFFFF 0%, #E0E0E0 50%, #D0D0D0 100%)",
      scrollButton: isDark
        ? "linear-gradient(180deg, #3A3A5A 0%, #2A2A4A 50%, #1A1A2E 100%)"
        : "linear-gradient(180deg, #FFFFFF 0%, #ECE9D8 50%, #D4D0C8 100%)",
      scrollBorder: isDark ? "#3A3A5A" : "#DFDFDF",
      scrollThumbBorder: isDark ? "#5A5A7A" : "#808080",
      scrollButtonLight: isDark ? "#5A5A7A" : "#FFFFFF",
      scrollButtonDark: isDark ? "#1A1A2E" : "#808080",
    },
    green: {
      bg: "#001100",
      text: "#00FF00",
      link: "#00FF00",
      linkHover: "#33FF33",
      selection: "#003300",
      scrollBg: "#002200",
      scrollThumb: "linear-gradient(90deg, #004400 0%, #003300 50%, #002200 100%)",
      scrollThumbHover: "linear-gradient(90deg, #005500 0%, #004400 50%, #003300 100%)",
      scrollButton: "linear-gradient(180deg, #003300 0%, #002200 50%, #001100 100%)",
      scrollBorder: "#003300",
      scrollThumbBorder: "#00FF00",
      scrollButtonLight: "#004400",
      scrollButtonDark: "#001100",
    },
    red: {
      bg: "#110000",
      text: "#FF6666",
      link: "#FF3333",
      linkHover: "#FF6666",
      selection: "#330000",
      scrollBg: "#220000",
      scrollThumb: "linear-gradient(90deg, #440000 0%, #330000 50%, #220000 100%)",
      scrollThumbHover: "linear-gradient(90deg, #550000 0%, #440000 50%, #330000 100%)",
      scrollButton: "linear-gradient(180deg, #330000 0%, #220000 50%, #110000 100%)",
      scrollBorder: "#330000",
      scrollThumbBorder: "#FF3333",
      scrollButtonLight: "#440000",
      scrollButtonDark: "#110000",
    },
  };

  const colors = colorSchemes[colorVariant] || colorSchemes.blue;


  return (
    <GlobalStyles
      styles={{
        "*": {
          fontFamily: '"Tahoma", "Arial", sans-serif !important',
        },
        "html, body": {
          backgroundColor: colors.bg,
          fontFamily: '"Tahoma", "Arial", sans-serif',
          color: colors.text,
        },
        "*::-webkit-scrollbar": {
          width: "16px",
          height: "16px",
        },
        "*::-webkit-scrollbar-track": {
          background: colors.scrollBg,
          border: `1px solid ${colors.scrollBorder}`,
        },
        "*::-webkit-scrollbar-thumb": {
          background: colors.scrollThumb,
          border: `1px solid ${colors.scrollThumbBorder}`,
          borderRadius: 0,
          "&:hover": {
            background: colors.scrollThumbHover,
          },
        },
        "*::-webkit-scrollbar-corner": {
          background: colors.scrollBg,
        },
        "*::-webkit-scrollbar-button": {
          background: colors.scrollButton,
          border: "1px solid",
          borderTopColor: colors.scrollButtonLight,
          borderLeftColor: colors.scrollButtonLight,
          borderRightColor: colors.scrollButtonDark,
          borderBottomColor: colors.scrollButtonDark,
          height: "16px",
          width: "16px",
        },
        "::selection": {
          backgroundColor: colors.selection,
          color: "#FFFFFF",
        },
        "a": {
          color: colors.link,
        },
        "a:hover": {
          color: colors.linkHover,
          textDecoration: "underline",
        },
      }}
    />
  );
};

// Global styles for retro scanline effect and additional styling
const createRetroGlobalStyles = (color = "green") => {
  const c = RETRO_COLORS[color] || RETRO_COLORS.green;
  return (
    <GlobalStyles
      styles={{
        "*": {
          scrollbarWidth: "thin",
          scrollbarColor: `${c.primary} #0a0a0a`,
        },
        "*::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "*::-webkit-scrollbar-track": {
          background: "#0a0a0a",
          border: `1px solid ${c.divider}`,
        },
        "*::-webkit-scrollbar-thumb": {
          background: c.primary,
          border: "none",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          background: c.primaryLight,
        },
        "button:focus-visible, a:focus-visible, input:focus-visible": {
          outline: `2px solid ${c.primary}`,
          outlineOffset: 3,
        },
        // Text selection color
        "::selection": {
          background: c.primary,
          color: "#000000",
        },
        // Link styling
        "a": {
          color: c.primary,
        },
        "a:hover": {
          color: c.primaryLight,
          textShadow: `0 0 8px rgba(${c.rgb}, 0.5)`,
        },
      }}
    />
  );
};

function DashboardWrapper() {
  const { user } = useContext(AuthContext);
  const { mode } = useContext(ColorModeContext);
  const [dashboardStyle, setDashboardStyle] = useState(() => {
    // The authenticated account owns the preference; another account's cache must not override it.
    return user?.dashboardStyle || "mapview";
  });

  // Update when user preference changes
  useEffect(() => {
    if (user?.dashboardStyle) {
      setDashboardStyle(user.dashboardStyle);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(DASHBOARD_STYLE_STORAGE_KEY, user.dashboardStyle);
      }
    }
  }, [user?.dashboardStyle]);

  // Listen for dashboard style sync events
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleSync = () => {
      const stored = window.localStorage.getItem(DASHBOARD_STYLE_STORAGE_KEY);
      if (stored) {
        setDashboardStyle(stored);
      }
    };

    window.addEventListener(DASHBOARD_STYLE_SYNC_EVENT, handleSync);
    window.addEventListener("storage", handleSync);

    return () => {
      window.removeEventListener(DASHBOARD_STYLE_SYNC_EVENT, handleSync);
      window.removeEventListener("storage", handleSync);
    };
  }, []);

  // Determine retro color based on dashboard style
  const retroColor = dashboardStyle === "retro-red" ? "red" : "green";
  const retroTheme = React.useMemo(() => createRetroTheme(retroColor), [retroColor]);
  const retroGlobalStyles = React.useMemo(() => createRetroGlobalStyles(retroColor), [retroColor]);
  
  // Windows XP theme - determine dark mode and color variant from ColorModeContext
  const xpDarkMode = THEME_MODES[mode]?.paletteMode === "dark";
  const xpColorVariant = mode === "greenBlack" ? "green" : mode === "redBlack" ? "red" : "blue";
  const xpTheme = React.useMemo(() => createWindowsXPTheme(xpDarkMode, xpColorVariant), [xpDarkMode, xpColorVariant]);
  const xpGlobalStyles = React.useMemo(() => createWindowsXPGlobalStyles(xpDarkMode, xpColorVariant), [xpDarkMode, xpColorVariant]);

  // Render Windows XP theme wrapper if XP style is selected
  if (dashboardStyle === "windowsxp") {
    return (
      <ThemeProvider theme={xpTheme}>
        <CssBaseline />
        {xpGlobalStyles}
        <Dashboard />
      </ThemeProvider>
    );
  }

  // Render retro theme wrapper if retro style is selected
  if (dashboardStyle === "retro" || dashboardStyle === "retro-red") {
    return (
      <ThemeProvider theme={retroTheme}>
        <CssBaseline />
        {retroGlobalStyles}
        <Dashboard />
      </ThemeProvider>
    );
  }

  // Default modern style - render Dashboard as-is
  return <Dashboard />;
}

export default DashboardWrapper;
